import { useMemo } from 'react';
import type { TutorialStep } from '@kaeliux/shared';
import { tutorialStepNeedsTeam, type tutorialProgressFromState } from '@kaeliux/game-engine';
import { useTutorialStore } from '../store/tutorialStore';
import { useMyAlliance } from './useMyAlliance';
import { useRoundStatus } from './useRoundStatus';

export const TUTORIAL_TEAM_POLL_MS = 60_000;

type TutorialTeamProgress = NonNullable<Parameters<typeof tutorialProgressFromState>[0]['team']>;

/**
 * The player's alliance as the tutorial judges it, or `undefined` while the
 * current step doesn't need it or it is still loading. Reads the same alliance
 * cache as the Alliance page, and only while `step` is one of the team
 * chapter's and the tutorial isn't disabled — a player still on the early
 * tutorial, or one who switched it off, never pays for the request.
 *
 * The alliance is polled once a minute only while the tutorial popup is open,
 * and never on the step that asks whether the round is won (it only needs the
 * alliance id there). With the popup closed the normal staleTime, mount and
 * focus refetches, plus the fleet store's Sun Attack invalidation, keep it
 * roughly current. The round (shared with the round banner, which polls it
 * app-wide) is read only for the step that asks whether it is won, so a
 * failing round query can't hold up the others.
 */
export function useTutorialTeamProgress(step: TutorialStep): TutorialTeamProgress | undefined {
    const tutorialDisabled = useTutorialStore((s) => s.disabled);
    const popupOpen = useTutorialStore((s) => s.isActive);
    const needed = tutorialStepNeedsTeam(step) && !tutorialDisabled;
    const needsRound = needed && step.completion.type === 'roundWon';
    const polling = popupOpen && !needsRound;
    const alliance = useMyAlliance({ enabled: needed, refetchInterval: polling ? TUTORIAL_TEAM_POLL_MS : false });
    const round = useRoundStatus({ enabled: needsRound });
    const detail = alliance.data;
    const winnerAllianceId = needsRound ? round.data?.winner?.allianceId : undefined;
    const loaded = needed && detail !== undefined && (!needsRound || round.data !== undefined);

    return useMemo(() => {
        if (!loaded) return undefined;
        if (detail === null) {
            return { inAlliance: false, hasTeamPlanet: false, sunAttackSent: false, controlledSuns: 0, wonRound: false };
        }
        return {
            inAlliance: true,
            hasTeamPlanet: detail.hasTeamPlanet ?? false,
            sunAttackSent: detail.sunAttackSent ?? false,
            controlledSuns: detail.alliance.sunCount,
            wonRound: winnerAllianceId !== undefined && winnerAllianceId === detail.alliance.id,
        };
    }, [loaded, detail, winnerAllianceId]);
}
